import { useSearchParams } from "react-router";

import FetchWrapper from "@/components/UI/FetchWraper";
import type { ExtraTabComponentProps } from "@/components/Templates/types";
import useIsFeatureFlagEnabled from "@/hooks/useIsFeatureFlagEnabled";
import { useFilteredTrialIds } from "@/hooks/useFilteredTrialIds";
import { usePortfolioTrials } from "@/hooks/usePortfolioTrials";

import { PortfolioMetricsRail } from "./PortfolioMetricsRail";
import {
  METRIC_URL_PARAM,
  filterMetricRowsToTrials,
  getEnabledMetricPanels,
  resolveActiveMetricPanel,
} from "./portfolioMetricPanels";

const TRIAL_IDENTIFIER_COLUMN = "trial_nct_id";

export default function PortfolioMetricsView({
  filters,
  metadata,
  datasetName = "portfolio-trials",
  endpointPrefix,
}: ExtraTabComponentProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isFlagEnabled = useIsFeatureFlagEnabled();
  const trials = usePortfolioTrials();

  const { trialIds } = useFilteredTrialIds({
    datasetName,
    identifierColumn: TRIAL_IDENTIFIER_COLUMN,
    filters,
    metadata,
    endpointPrefix,
  });

  const panels = getEnabledMetricPanels(isFlagEnabled);
  const activePanel = resolveActiveMetricPanel(
    panels,
    searchParams.get(METRIC_URL_PARAM),
  );

  if (!activePanel) {
    return (
      <div className="flex h-48 items-center justify-center text-[13px] text-[#8C8C8C]">
        No portfolio metrics are enabled for this tenant.
      </div>
    );
  }

  const selectMetric = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(METRIC_URL_PARAM, key);
    setSearchParams(next);
  };

  const PanelComponent = activePanel.component;

  return (
    <div className="flex items-start gap-3.5">
      <PortfolioMetricsRail
        panels={panels}
        activeKey={activePanel.key}
        onSelect={selectMetric}
        trialCount={trialIds ? trialIds.size : (trials?.length ?? 0)}
      />

      <div className="min-w-0 flex-1">
        <FetchWrapper endpointSuffix={activePanel.endpointSuffix}>
          {(data) => (
            <PanelComponent
              data={filterMetricRowsToTrials(
                data,
                trialIds,
                activePanel.rowTrialField,
                trials,
              )}
              trials={trials}
            />
          )}
        </FetchWrapper>
      </div>
    </div>
  );
}
